import { useCallback, useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import {
  thicknessConditionsSchema,
  thicknessPresetNameSchema,
} from '@/features/cad/schemas/thicknessConditions.schema';
import {
  createThicknessPreset,
  listThicknessPresets,
} from '../../../services/cad.service';
import {
  ThicknessAnalysisProfile,
  ThicknessConfig,
  ThicknessPreset,
} from '../../../types/cad';
import { resolveApiErrorMessage } from '../../../utils/apiErrorMessages';

interface UseThicknessConfigArgs {
  t: TFunction;
  setError: (message: string | null) => void;
}

/**
 * Owns the editable analysis condition fields and the preset catalog that
 * reads/writes them. Kept together because selecting a preset applies its
 * config and saving a preset captures the current config values.
 */
export const useThicknessConfig = ({ t, setError }: UseThicknessConfigArgs) => {
  const [lowerMm, setLowerMm] = useState(2);
  const [upperMm, setUpperMm] = useState(3);
  const [toleranceMm, setToleranceMm] = useState(0);
  const [clusterRadiusMm, setClusterRadiusMm] = useState(3);
  const [clusterMinPoints, setClusterMinPoints] = useState(3);
  const [profile, setProfile] = useState<ThicknessAnalysisProfile>('standard');
  const [presets, setPresets] = useState<ThicknessPreset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);

  useEffect(() => {
    listThicknessPresets()
      .then(setPresets)
      .catch((presetError) => {
        setPresets([]);
        setError(
          resolveApiErrorMessage(t, presetError, {
            fallbackKey: 'cad.thickness.preset.loadFailed',
          }),
        );
      });
  }, [t, setError]);

  const applyConfig = useCallback((config: ThicknessConfig) => {
    setLowerMm(config.lowerMm);
    setUpperMm(config.upperMm);
    setToleranceMm(config.judgmentToleranceMm);
    setClusterRadiusMm(config.clusterRadiusMm);
    setClusterMinPoints(config.clusterMinPoints);
    setProfile(config.profile);
  }, []);

  const selectPreset = useCallback((presetId: string) => {
    setSelectedPresetId(presetId);
    const selected = presets.find((item) => item.presetId === presetId);
    if (!selected) return;
    setPresetName(selected.name);
    applyConfig(selected.config);
  }, [applyConfig, presets]);

  const savePreset = useCallback(async () => {
    const conditions = thicknessConditionsSchema.safeParse({
      lowerMm,
      upperMm,
      toleranceMm,
    });
    if (!conditions.success) {
      setError(conditions.error.issues[0].message);
      return;
    }
    // parsed.data is the trimmed name and is what gets sent, so the schema
    // validates exactly the value the API receives.
    const parsed = thicknessPresetNameSchema.safeParse(presetName);
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    const name = parsed.data;
    const validatedConditions = conditions.data;
    setSavingPreset(true);
    try {
      const saved = await createThicknessPreset(
        name,
        {
          lowerMm: validatedConditions.lowerMm,
          upperMm: validatedConditions.upperMm,
          judgmentToleranceMm: validatedConditions.toleranceMm,
          finalLowerMm: validatedConditions.lowerMm - validatedConditions.toleranceMm,
          finalUpperMm: validatedConditions.upperMm + validatedConditions.toleranceMm,
          clusterRadiusMm,
          clusterMinPoints,
          profile,
        },
        selectedPresetId || null,
      );
      setPresets((current) => [
        saved,
        ...current.filter((item) => item.presetId !== saved.presetId),
      ]);
      setSelectedPresetId(saved.presetId);
      setPresetName(saved.name);
      setError(null);
    } catch (presetError) {
      setError(
        resolveApiErrorMessage(t, presetError, {
          fallbackKey: 'cad.thickness.preset.saveFailed',
        }),
      );
    } finally {
      setSavingPreset(false);
    }
  }, [
    clusterMinPoints,
    clusterRadiusMm,
    lowerMm,
    presetName,
    profile,
    selectedPresetId,
    setError,
    t,
    toleranceMm,
    upperMm,
  ]);

  return {
    lowerMm,
    upperMm,
    toleranceMm,
    clusterRadiusMm,
    clusterMinPoints,
    profile,
    setLowerMm,
    setUpperMm,
    setToleranceMm,
    setProfile,
    applyConfig,
    presets,
    selectedPresetId,
    presetName,
    setPresetName,
    savingPreset,
    selectPreset,
    savePreset,
  };
};
